import {
    Fragment,
    useEffect,
    useState,
    useCallback
} from "react";
import { useSelector } from "react-redux";
import {
    Link,
    useLocation,
    useNavigate,
    useParams
} from "react-router-dom";

import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

import {
    ArrowUpTrayIcon,
    PencilSquareIcon,
    TrashIcon
} from "@heroicons/react/24/outline";
import {
    BrowserIcon,
    FileCodeIcon,
    MarkGithubIcon
} from "@primer/octicons-react";

import {
    selectEnv,
    selectIsSidebarLarge,
    selectMemberships,
    selectSelfUrl,
    selectUser
} from "../lib/scraper.slice";
import {
    IEndpointSlim,
    ILookupTable,
    ITableRowFilter,
    IPrimitiveType
} from "../lib/backend/extractions.types.generated";
import { IMembership } from "../lib/types";
import {
    BackendObj
} from "../lib/backend";
import {
    classNames,
    escapeCsvField,
    setDocumentTitle
} from "../lib/utils";

import { ITab, Tabs } from "../components/Tabs";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { ORG_TYPES } from "../lib/consts";
import { Button, ButtonGroup } from "../components/Button";
import { CopyTextbox } from "../components/CopyTextbox";
import { VirtualizedDataGrid } from "../components/VirtualizedDataGrid";
import { ConfirmModal } from "../components/ConfirmModal";
import { TwoRowHeader } from "../components/Header";
import { ListSourceIPs } from "../components/ListSourceIPs";
import { ButtonMenu } from "../components/ButtonMenu";


export function LookupTableDetail() {
    const navigate = useNavigate();
    const location = useLocation();
    const { lookup_table_uuid } = useParams<{ lookup_table_uuid: string | undefined }>();

    // Get tab and page from URL query params, defaulting to "extract_jobs" and 0
    const query_params = new URLSearchParams(location.search);
    const selected_tab = query_params.get("tab") || "observed_values";

    const env = useSelector(selectEnv);
    const self_url = useSelector(selectSelfUrl);
    const user = useSelector(selectUser);
    const is_sidebar_large = useSelector(selectIsSidebarLarge);
    const memberships: IMembership[] = useSelector(selectMemberships);

    const [lookup_table, setLookupTable] = useState<ILookupTable | undefined>(undefined);
    const [lookup_table_is_pinned, setLookupTableIsPinned] = useState(false);
    const [total_row_count, setTotalRowCount] = useState<number>(0);
    const [is_deleting, setIsDeleting] = useState(false);
    const [is_downloading, setIsDownloading] = useState(false);
    const [show_confirm, setShowConfirm] = useState(false);
    const [connected_endpoints, setConnectedEndpoints] = useState<IEndpointSlim[]>([]);

    const is_org_admin = memberships.filter((m) => m.org.uuid === lookup_table?.org_uuid && m.role === "admin").length > 0;

    // Server-side data loading function
    const loadTableData = useCallback(async (offset: number, limit: number, filters: ITableRowFilter[]) => {
        if (!lookup_table_uuid) throw new Error("No lookup table UUID");

        const { rows } = await BackendObj.extractions.listLookupTableRows({
            lookup_table_uuid,
            offset,
            limit,
            filters
        });

        return { rows };
    }, [lookup_table_uuid]);

    useEffect(() => {
        if (lookup_table_uuid === undefined) { return; }
        BackendObj.extractions.getLookupTable({ lookup_table_uuid })
            .then(({ lookup_table }) => {
                if (lookup_table !== undefined) {
                    setLookupTable(lookup_table);
                    setTotalRowCount(lookup_table.no_of_rows || 0);
                    setLookupTableIsPinned(lookup_table.is_pinned ?? false);
                }
            });
        BackendObj.extractions.getEndpointsWithActionsForLookupTable({ lookup_table_uuid })
            .then(({ endpoints }) => {
                setConnectedEndpoints(endpoints);
            });
    }, [lookup_table_uuid]);

    useEffect(() => {
        if (lookup_table === undefined) {
            setDocumentTitle("Table", env);
        } else {
            setDocumentTitle(`Table - ${lookup_table.name}`, env);
        }
    }, [lookup_table, env]);

    const is_rest_api_enabled = lookup_table?.details.rest_api_update || lookup_table?.details.rest_api_get;

    const tabs: ITab[] = [
        { name: "Data", key: "observed_values" },
        { name: "Details", key: "details" },
        { name: "API Docs", key: "api_docs", hide: !(is_rest_api_enabled && is_org_admin) }
    ];

    const toggleLookupTablePin = async () => {
        if (lookup_table_uuid === undefined) { return; }
        if (!lookup_table_is_pinned) {
            await BackendObj.extractions.addElementToPinboard({
                pinned_type: "lookup_table",
                pinned_uuid: lookup_table_uuid
            });
            setLookupTableIsPinned(true);
        } else {
            await BackendObj.extractions.removeElementFromPinboard({ pinned_uuid: lookup_table_uuid });
            setLookupTableIsPinned(false);
        }
    }

    const onDownload = async (format: "csv" | "json-aoa" | "json-aos" | "excel") => {
        if (lookup_table === undefined) { return; }
        setIsDownloading(true);

        // For downloads, we need to fetch all data
        const all_rows = await BackendObj.extractions.listLookupTableRows({
            lookup_table_uuid: lookup_table.uuid
        });

        const filename = `table_${lookup_table.name.replace(/[^a-zA-Z0-9_]/g, "")}`;

        if (format === "csv") {
            // determine delimiter based on org tag
            const use_semicolon = lookup_table_org?.org.tags?.csv_use_semicolon ?? false;
            const delimiter = use_semicolon ? ";" : ",";
            // format numbers with german locale for semicolon CSV
            const format_value = (value: any): string => {
                if (use_semicolon && typeof value === "number") {
                    return value.toLocaleString("de-DE", { useGrouping: false });
                }
                return String(value);
            };
            // prepare aoa data
            const data: IPrimitiveType[][] = [lookup_table.headers.map((header) => header.name)];
            all_rows.rows.forEach((row) => {
                data.push(lookup_table.headers.map((header) => row.data[header.uuid] ?? ""));
            });
            // create CSV content with appropriate delimiter
            const csv_content = data.map((row) =>
                row.map((value) => escapeCsvField(format_value(value), delimiter)).join(delimiter)
            ).join("\n");
            // add BOM for semicolon CSV
            const bom = use_semicolon ? "\uFEFF" : "";
            const blob = new Blob([bom + csv_content], { type: "text/csv" });
            saveAs(blob, `${filename}.csv`);

        } else if (format === "json-aoa") {
            const data: IPrimitiveType[][] = [lookup_table.headers.map((header) => header.name)];
            all_rows.rows.forEach((row) => {
                data.push(lookup_table.headers.map((header) => row.data[header.uuid] ?? ""));
            });
            const json_content = JSON.stringify(data, null, 2);
            const blob = new Blob([json_content], { type: "application/json" });
            saveAs(blob, `${filename}.json`);

        } else if (format === "json-aos") {
            const data: { [key: string]: IPrimitiveType }[] = [];
            all_rows.rows.forEach((row) => {
                data.push(lookup_table.headers.reduce((acc, header) => {
                    acc[header.name] = row.data[header.uuid] ?? "";
                    return acc;
                }, {} as { [key: string]: IPrimitiveType }));
            });
            const json_content = JSON.stringify(data, null, 2);
            const blob = new Blob([json_content], { type: "application/json" });
            saveAs(blob, `${filename}.json`);

        } else if (format === "excel") {
            const data: IPrimitiveType[][] = [lookup_table.headers.map((header) => header.name)];
            all_rows.rows.forEach((row) => {
                data.push(lookup_table.headers.map((header) => row.data[header.uuid] ?? ""));
            });
            const ws = XLSX.utils.aoa_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
            const excel_blob = new Blob(
                [XLSX.write(wb, { bookType: "xlsx", type: "array" })],
                { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
            );
            saveAs(excel_blob, `${filename}.xlsx`);
        }

        setIsDownloading(false);
    };

    const onAddRow = async (row: string[]) => {
        if (lookup_table === undefined) { return; }
        await BackendObj.extractions.addLookupTableRow({ lookup_table_uuid: lookup_table.uuid, row });
        // Update total count
        setTotalRowCount(prev => prev + 1);
    }

    const onDeleteRow = async (row_uuid: string) => {
        if (lookup_table === undefined) { return; }
        await BackendObj.extractions.deleteLookupTableRow({ row_uuid, lookup_table_uuid: lookup_table.uuid });
        // Update total count
        setTotalRowCount(prev => prev - 1);
    }

    const onUpdateRow = async (row_uuid: string, row: string[]) => {
        if (lookup_table === undefined) { return; }
        await BackendObj.extractions.updateLookupTableRow({ row_uuid, row, lookup_table_uuid: lookup_table.uuid });
    }

    const onRemoveClose = async (is_remove: boolean) => {
        setShowConfirm(false);
        if (is_remove && lookup_table_uuid !== undefined) {
            setIsDeleting(true);
            // delete from backend
            await BackendObj.extractions.deleteLookupTable({ lookup_table_uuid });
            // navigate back to list
            navigate("/knowledge-base");
        }
    };

    if (lookup_table === undefined) {
        return <div className={classNames("hidden lg:fixed lg:right-0 lg:inset-y-0 lg:flex lg:flex-row", is_sidebar_large ? "lg:left-64" : "lg:left-20")}>
            <LoadingSpinner />
        </div>;
    }

    const is_email_enabled = lookup_table.details.email_update !== undefined && lookup_table.details.email_update !== "disabled";
    const is_personal = memberships.some((m) => m.org.uuid === lookup_table.org_uuid && m.org.type === ORG_TYPES.personal);

    const hash_keys = lookup_table.details.merge_key_column_uuids?.map((uuid) => lookup_table.headers.find((header) => header.uuid === uuid)?.name) || [];
    const compensate_leading_zeros_keys = lookup_table.details.compensate_leading_zeros_column_uuids?.map((uuid) => lookup_table.headers.find((header) => header.uuid === uuid)?.name) || [];

    const lookup_table_org = memberships.find(({ org }) => org.uuid === lookup_table.org_uuid);

    return <div className={classNames("flex flex-col h-screen lg:fixed lg:right-0 lg:inset-y-0 lg:overflow-auto", is_sidebar_large ? "lg:left-64" : "lg:left-20")}>
        <TwoRowHeader
            title={lookup_table.name}
            subtitle="Table"
            org_name={lookup_table_org?.org.name}
            buttons={<Fragment>
                <ButtonMenu
                    title="Download"
                    items={[
                        { title: "CSV", onClick: () => onDownload("csv") },
                        { title: "JSON (array of arrays)", onClick: () => onDownload("json-aoa") },
                        { title: "JSON (array of objects)", onClick: () => onDownload("json-aos") },
                        { title: "Excel", onClick: () => onDownload("excel") }
                    ]}
                    is_processing={is_downloading}
                />
                {lookup_table && is_org_admin && <ButtonGroup disabled={is_deleting}
                    buttons={[
                        { icon: ArrowUpTrayIcon, text: "", tooltip: "Upload", href: `/lookup_table/upload/${lookup_table.uuid}` },
                        { icon: PencilSquareIcon, text: "", tooltip: "Edit", href: `/lookup_table/edit/${lookup_table.uuid}` },
                        { icon: TrashIcon, text: "", tooltip: "Remove", onClick: () => setShowConfirm(true) }
                    ]} />}
            </Fragment>}
            pinned={lookup_table_is_pinned}
            onPin={toggleLookupTablePin}
            onBack={() => navigate("/knowledge-base")}
        />

        <div className="px-10 max-w-5xl flex-shrink-0">
            <div className="py-6">
                <Tabs tabs={tabs} selected_tab_key={selected_tab} setSelectedTab={(tab) => navigate(`/lookup_table/${lookup_table_uuid}?tab=${tab}`)} />
            </div>

            {selected_tab === "details" && <Fragment>
                <div className="pt-3 pb-6 sm:grid sm:grid-cols-10 sm:gap-x-4 sm:gap-y-6 sm:px-0 items-center border-b border-gray-200">
                    <dt className="text-sm font-medium leading-6 text-gray-900 sm:col-span-3">Alias</dt>
                    <dd className="flex fle-row items-center pr-4 text-sm leading-6 text-gray-500 sm:col-span-7 sm:mt-0">
                        {lookup_table.alias || "/"}
                    </dd>
                    <dt className="text-sm font-medium leading-6 text-gray-900 sm:col-span-3">Merge keys</dt>
                    <dd className="pr-4 text-sm leading-6 text-gray-500 sm:col-span-7 sm:mt-0">
                        {hash_keys.length === 0 && "/"}
                        {hash_keys.length > 0 && hash_keys.join(", ")}
                    </dd>
                    <dt className="text-sm font-medium leading-6 text-gray-900 sm:col-span-3">Compensate leading zeros</dt>
                    <dd className="pr-4 text-sm leading-6 text-gray-500 sm:col-span-7 sm:mt-0">
                        {compensate_leading_zeros_keys.length === 0 && "/"}
                        {compensate_leading_zeros_keys.length > 0 && compensate_leading_zeros_keys.join(", ")}
                    </dd>
                    <dt className="text-sm font-medium leading-6 text-gray-900 sm:col-span-3">Connected endpoints</dt>
                    <dd className="pr-4 text-sm leading-6 text-gray-500 sm:col-span-7 sm:mt-0">
                        {connected_endpoints.length === 0 && "/"}
                        {connected_endpoints.length > 0 && connected_endpoints.map((endpoint) => <Link to={`/endpoint/${endpoint.uuid}`} className="hover:underline">{endpoint.name}</Link>)}
                    </dd>
                </div>

                {is_email_enabled && <div className="py-6 sm:grid sm:grid-cols-10 sm:gap-x-4 sm:gap-y-6 sm:px-0 items-center border-b border-gray-200">
                    <dt className="text-sm font-medium leading-6 text-gray-900 sm:col-span-2">Email connector</dt>
                    <dd className="pr-4 text-sm leading-6 text-space_blue-600 sm:col-span-8 sm:mt-0">
                        <CopyTextbox text={lookup_table.email_address} email_pretty_name={`Update table '${lookup_table.name}'`} is_email={true} />
                    </dd>
                    <dt className="text-sm font-medium leading-6 text-gray-900 sm:col-span-3">Upload via Email</dt>
                    <dd className="pr-4 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                        {[
                            { key: "replace", name: "Upload" },
                            { key: "append", name: "Append" },
                            { key: "merge_incoming_wins", name: "Merge incoming wins" },
                            { key: "merge_existing_wins", name: "Merge existing wins" }
                        ].find((f) => f.key === lookup_table.details.email_update)?.name || "/"}
                    </dd>
                    <dt className="text-sm font-medium leading-6 text-gray-900 sm:col-span-3">Attachment format</dt>
                    <dd className="pr-4 text-sm leading-6 text-gray-500 sm:col-span-2 sm:mt-0">
                        {[
                            { key: "tsv", name: "TSV" },
                            { key: "csv", name: "CSV" }
                        ].find((f) => f.key === lookup_table.details.email_attachment_format)?.name || "TSV"}
                    </dd>
                </div>}

                {is_rest_api_enabled && <div className="py-6 sm:grid sm:grid-cols-10 sm:gap-x-4 sm:gap-y-6 sm:px-0 items-center border-b border-gray-200">
                    <dt className="text-sm font-medium leading-6 text-gray-900 sm:col-span-2">REST API Endpoint</dt>
                    <dd className="pr-4 text-sm leading-6 sm:col-span-8 sm:mt-0 text-space_blue-600">
                        <CopyTextbox text={lookup_table.url} is_email={false} />
                    </dd>
                </div>}
            </Fragment>}

            {selected_tab === "api_docs" && <Fragment>
                <h3 className="px-4 pt-4 leading-7 text-lg font-medium">API Keys</h3>
                <div className="text-sm text-gray-500 flex-grow py-4">
                    {!is_personal && <span>You can manage API keys in the <Link to={`/org/${lookup_table.org_uuid}`} className="text-space_blue-600 underline">organization settings</Link>.</span>}
                    {is_personal && <span>You can manage API keys in the <Link to={`/user/${user.uuid}`} className="text-space_blue-600 underline">user settings</Link>.</span>}
                </div>
                <h3 className="px-4 pt-4 leading-7 text-lg font-medium">REST API</h3>
                <div className="my-4 flex flex-row gap-2 items-center">
                    <Button
                        icon={FileCodeIcon}
                        text="OpenAPI"
                        href={`${self_url}/public/open-api.json`}
                        open_in_new_tab={true} />
                    <Button
                        icon={BrowserIcon}
                        text="Swagger"
                        href={`${self_url}/public/swagger`}
                        open_in_new_tab={true} />
                    <ListSourceIPs />
                    <Button
                        icon={MarkGithubIcon}
                        href="https://gist.github.com/blazf/3bb9abf9bab1d968b06413c36005337e"
                        text="Node.JS example"
                        open_in_new_tab={true} />
                </div>
            </Fragment>}
        </div>

        {selected_tab === "observed_values" &&
            <div className="flex-1 px-10 pb-6 min-h-0">
                <VirtualizedDataGrid
                    headers={lookup_table.headers}
                    total_rows={total_row_count}
                    onLoadData={loadTableData}
                    onAddRow={onAddRow}
                    onDeleteRow={onDeleteRow}
                    onUpdateRow={onUpdateRow}
                    page_size={100}
                    show_actions_header={true}
                    show_row_numbers={true}
                    full_height={true}
                />
            </div>}

        <ConfirmModal open={show_confirm}
            title="Remove Table"
            message={["Are you sure you want to remove this table?"]}
            confirm="Remove"
            onClose={onRemoveClose} />
    </div>;
}
